import React, { useMemo, useState } from "react";

import { Localized, useLocalization } from "@fluent/react";
import { useDocumentFilters } from "byzantine/src/dbbl/businessLogic/entities/documents/hooks";
import {
  ContentCard,
  Pagination,
  Row,
  useBreakpoints,
} from "@narmi/design_system";
import cc from "classcat";
import { useAccounts } from "byzantine/src/dbbl/businessLogic/entities/accounts";

import utils from "../../../utils";
import PageLayout from "../../components/PageLayout";
import { useDragHorizontalScroll } from "../../../components/hooks/useDragHorizontalScroll";

import DocumentFilterAnchoredDialog from "./DocumentFilterAnchoredDialog/DocumentFilterAnchoredDialog";
import DocumentFilter from "./components/DocumentFilter";
import styles from "./DocumentsPage.module.scss";
import { CheckboxProvider } from "./components/CheckboxContext";
import DocumentsList from "./components/DocumentsList";
import DocumentsTable from "./DocumentsTable/DocumentsTable";
import { DOCUMENTS_PER_PAGE } from "./constants";
import { usePaginatedDocuments } from "./hooks/usePaginatedDocuments";
import DocumentShareDialog from "./components/DocumentShareDialog";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

const getTotalPages = ({
  isApiPaginationActive,
  currentMaxPage,
  totalPages,
  filteredDocumentsLength,
}: {
  isApiPaginationActive: boolean;
  currentMaxPage: number;
  totalPages?: number;
  filteredDocumentsLength: number;
}): number => {
  if (isApiPaginationActive) {
    return currentMaxPage;
  }

  return totalPages ?? Math.ceil(filteredDocumentsLength / DOCUMENTS_PER_PAGE);
};

const DocumentsPage = ({
  documentsJson,
  isHtmlJson,
  totalPages,
}: {
  documentsJson: string;
  isHtmlJson: "true" | "false";
  totalPages?: number;
}) => {
  const { m: isPastTableBreakpoint } = useBreakpoints({
    m: "(min-width: 1010px)",
  });
  const documents = JSON.parse(utils.unescape(documentsJson));
  const { l10n } = useLocalization();

  const [activePage, setActivePage] = useState(1);
  const [isTypesOpen, setIsTypesOpen] = useState(false);
  const [isYearsOpen, setIsYearsOpen] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);
  const [isShareDialogOpen, setIShareDialogOpen] = useState<boolean>(false);
  const openDocumentShareDialog = () => setIShareDialogOpen(true);
  const toggleShareDialogHandler = () => setIShareDialogOpen((prev) => !prev);
  const accounts = useAccounts();

  const {
    ref: filtersScrollRef,
    isDragging,
    isScrollable,
    handlers,
  } = useDragHorizontalScroll<HTMLDivElement>({
    dragThresholdPx: 6,
    translateVerticalWheel: true,
    enableOnTouch: false, // keep native touch scroll
  });

  // 1. If we're using backend pagination, track accumulated documents
  const {
    documents: accumulatedDocuments,
    currentMaxPage,
    isApiPaginationActive,
    loadNextPage,
  } = usePaginatedDocuments(documents, () => {
    setActivePage(
      Math.trunc((filteredDocuments.length - 1) / DOCUMENTS_PER_PAGE) + 1
    );
  });

  // 2. Apply filters to documents
  const {
    filteredDocuments,
    filterChoices,
    activeFilters,
    setActiveFilters,
    clearFilters,
  } = useDocumentFilters<WebDocument>(accumulatedDocuments);

  // 3. Paginate over documents
  const filteredDocumentsPage = useMemo(
    () =>
      filteredDocuments.slice(
        DOCUMENTS_PER_PAGE * (activePage - 1),
        DOCUMENTS_PER_PAGE * activePage
      ),
    [filteredDocuments, activePage]
  );

  // Since a single document label may reference multiple types, we need to
  // compute the number of selected document type labels.
  const selectedDocumentTypeLabels = useMemo(
    () =>
      [...activeFilters.typeLabels].reduce((knownTypeLabels, type) => {
        Object.entries(filterChoices.typeLabels).forEach(([label, value]) => {
          if (value.includes(type)) {
            knownTypeLabels.add(label);
          }
        });
        return knownTypeLabels;
      }, new Set<string>()),
    [activeFilters.typeLabels, filterChoices.typeLabels]
  );

  const documentTypeItems = Object.entries(filterChoices.typeLabels).map(
    ([label, value]) => ({
      key: value,
      name: label,
    })
  );
  const documentTypeContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-type-popover-heading",
        null,
        "Document"
      )}
      activeFilters={[...activeFilters.typeLabels]}
      items={documentTypeItems}
      onApply={(keys) => {
        setActiveFilters((prev) => ({ ...prev, typeLabels: new Set(keys) }));
        setIsTypesOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsTypesOpen}
      isOpen={isTypesOpen}
      trigger={
        <DocumentFilter
          kind="type"
          count={selectedDocumentTypeLabels.size || undefined}
          onClick={() => setIsTypesOpen(true)}
        />
      }
    />
  );

  const accountNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    Object.entries(filterChoices.accounts).forEach(([id, name]) => {
      const account = accounts.find((acc) => acc.id === id);
      lookup[id] = account?.getShortDescription(false) || name;
    });
    return lookup;
  }, [filterChoices.accounts, accounts]);

  const documentAccountItems = Object.entries(filterChoices.accounts).map(
    ([id, name]) => {
      return {
        key: id,
        name: accountNameLookup[id] ?? name,
      };
    }
  );

  const documentAccountContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-account-popover-heading",
        null,
        "Account"
      )}
      activeFilters={[...activeFilters.accounts]}
      items={documentAccountItems}
      onApply={(keys) => {
        setActiveFilters((prev) => ({ ...prev, accounts: new Set(keys) }));
        setIsAccountsOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsAccountsOpen}
      isOpen={isAccountsOpen}
      trigger={
        <DocumentFilter
          kind="account"
          count={activeFilters.accounts.size || undefined}
          onClick={() => setIsAccountsOpen(true)}
        />
      }
    />
  );

  const documentYearContent = (
    <DocumentFilterAnchoredDialog
      heading={l10n.getString(
        "document-filter-year-popover-heading",
        null,
        "Year"
      )}
      activeFilters={[...activeFilters.years]}
      items={filterChoices.years.map((year) => ({
        key: year,
        name: year,
      }))}
      onApply={(keys) => {
        setActiveFilters((prev) => ({ ...prev, years: new Set(keys) }));
        setIsYearsOpen(false);
        setActivePage(1);
      }}
      setIsOpen={setIsYearsOpen}
      isOpen={isYearsOpen}
      trigger={
        <DocumentFilter
          kind="year"
          count={activeFilters.years.size || undefined}
          onClick={() => setIsYearsOpen(true)}
        />
      }
    />
  );

  const WrapperElement = isPastTableBreakpoint ? "div" : ContentCard;

  const totalPageCount = getTotalPages({
    isApiPaginationActive,
    currentMaxPage,
    totalPages,
    filteredDocumentsLength: filteredDocuments.length,
  });

  const filtersRowClassName = cc([
    styles["documents-page-filters-row"],
    isDragging && styles.dragging,
  ]);

  return (
    <PageLayout
      header={
        <div className={styles.header}>
          <h2>{l10n.getString("heading-statements")}</h2>
        </div>
      }
    >
      <CheckboxProvider>
        <div className={styles["documents-page-filters"]}>
          <div
            className={filtersRowClassName}
            ref={filtersScrollRef}
            {...handlers}
          >
            <Row gapSize="xs" alignItems="center">
              {documentTypeContent}
              {documentAccountContent}
              {documentYearContent}
              {(activeFilters.typeLabels.size > 0 ||
                activeFilters.years.size > 0 ||
                activeFilters.accounts.size > 0) && (
                <button
                  onClick={() => {
                    setActivePage(1);
                    clearFilters();
                  }}
                  className={styles["documents-page-clear-all-button"]}
                  data-testid="documents-clear-all-button"
                >
                  <Localized id="document-filter-clear-all">
                    Clear all
                  </Localized>
                </button>
              )}
              {isScrollable && (
                <>
                  <div
                    className={
                      styles["documents-page-filters-row-gradient-spacer"]
                    }
                  />
                  <div
                    className={styles["documents-page-filters-row-gradient"]}
                  />
                </>
              )}
            </Row>
          </div>
        </div>
        <WrapperElement kind="bordered">
          {isPastTableBreakpoint ? (
            <DocumentsTable
              documents={filteredDocumentsPage}
              isHtmlJson={isHtmlJson === "true"}
              openDocumentShareDialog={openDocumentShareDialog}
            />
          ) : (
            <DocumentsList
              documents={filteredDocumentsPage}
              isHtmlJson={isHtmlJson === "true"}
            />
          )}

          <Pagination
            testId="documents-pagination"
            totalPages={totalPageCount}
            selectedPage={activePage}
            onPageChange={(newPage: number) => {
              // If we're using backend pagination and we've navigated to a page that is at or past our
              // current max page, attempt to load the next page of documents.
              if (isApiPaginationActive && newPage >= currentMaxPage) {
                loadNextPage();
              }
              setActivePage(newPage);
            }}
          />

          <DocumentShareDialog
            isOpen={isShareDialogOpen}
            onUserDismiss={toggleShareDialogHandler}
          />
        </WrapperElement>
      </CheckboxProvider>
    </PageLayout>
  );
};

export default DocumentsPage;
